import { FieldPath, TypeAtPath } from "./amp-list-utils";

export type MustacheBlockType = "if" | "unless" | "foreach";

export const semanticToSyntactic: Record<MustacheBlockType, string> = {
  if: "#",
  foreach: "#", // Same as if, but communicates intent
  unless: "^",
};

const END_BLOCK = "/";

export type VariableFunction<T> = (
  path: FieldPath<T>,
  prefix?: string,
) => `{{${string}}}`;

export type ItemType<T, Path extends FieldPath<T>> =
  TypeAtPath<T, Path> extends Array<infer U> ? U : TypeAtPath<T, Path>;

export type NestedVariableFunction<
  T,
  Path extends FieldPath<T>,
> = VariableFunction<ItemType<T, Path>>;

export interface MustacheContext<T, Path extends FieldPath<T>> {
  var: NestedVariableFunction<T, Path>;
}

export function createMustacheContext<
  T,
  Path extends FieldPath<T>,
>(): MustacheContext<T, Path> {
  return { var: (subPath, prefix) => ampVariable(subPath, prefix) };
}

export function ampVariable(path: string, prefix?: string): `{{${string}}}` {
  return `{{${prefix ?? ""}${path}}}` as `{{${string}}}`;
}

/**
 * @param path - Make sure you use `as const` to ensure the path is a string literal.
 */
export function MustacheSection<
  T,
  Path extends FieldPath<T>,
  SubPath extends FieldPath<ItemType<T, Path>>,
>({
  context, // used for inferring types
  path,
  type,
  children,
}: { context: MustacheContext<T, Path>; path: SubPath } & (
  | { type: Exclude<MustacheBlockType, "foreach">; children: React.ReactNode }
  | {
      type: "foreach";
      children: (
        subContext: MustacheContext<ItemType<T, Path>, SubPath>,
      ) => React.ReactNode;
    }
)) {
  const subScope = createMustacheContext<ItemType<T, Path>, SubPath>();

  return (
    <>
      {ampVariable(path, semanticToSyntactic[type])}
      {type === "foreach" ? children(subScope) : children}
      {ampVariable(path, END_BLOCK)}
    </>
  );
}
